import { VAULT_CONTRACT_ADDRESS_KEY } from "@constants";
import { resetEkidenSdkClient, setConfigReady, setVaultContract, updateAptosClient } from "@src/api";
import { resetConnectionState } from "@src/api/ws/private-subscriptions";
import { useStores } from "@stores/useStores";
import { initializeGasStation } from "@utils/gasStation";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type PropsWithChildren, useEffect } from "react";

export const ConfigProvider: React.FC<PropsWithChildren> = observer(({ children }) => {
  const { configStore, accountStore } = useStores();

  useEffect(() => {
    const init = async () => {
      await configStore.fetchConfig();

      if (configStore.config) {
        const newVaultAddress = configStore.vaultAddress;
        const storedVaultAddress = localStorage.getItem(VAULT_CONTRACT_ADDRESS_KEY);

        if (storedVaultAddress && storedVaultAddress !== newVaultAddress) {
          await accountStore.disconnect({ vaultContractKey: newVaultAddress });
        }

        localStorage.setItem(VAULT_CONTRACT_ADDRESS_KEY, newVaultAddress);

        setVaultContract(newVaultAddress);
        setConfigReady(true);
        resetConnectionState();
        resetEkidenSdkClient();
        updateAptosClient(configStore.aptosNetwork, configStore.aptosApiKey);

        if (configStore.gasStationApiKey) {
          initializeGasStation(configStore.gasStationApiKey);
        }

        // Must be called last: triggers all reactions that depend on a ready SDK
        // (WS connection, order loading, etc.). By this point setConfigReady(true)
        // and resetEkidenSdkClient() have already run, so the next
        // getEkidenSdkClient() call will create a client with proper WS URLs and
        // the current auth token already set on it.
        configStore.setSdkReady(true);
      }
    };

    init();
  }, [configStore, accountStore]);

  // Always render children - components will show skeletons based on configStore.isConfigAvailable
  return <>{children}</>;
});
